import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CheckCircle, CaretDown, CaretRight, Sparkle, ArrowSquareOut, WarningCircle } from "@phosphor-icons/react";

const STATE_OPTIONS = [
  ["MH", "Maharashtra"], ["KA", "Karnataka"], ["TN", "Tamil Nadu"],
  ["DL", "Delhi"], ["GJ", "Gujarat"], ["TG", "Telangana"], ["WB", "West Bengal"],
  ["HR", "Haryana"], ["UP", "Uttar Pradesh"], ["RJ", "Rajasthan"], ["KL", "Kerala"],
];

/**
 * PayrollSetup — one-time payroll setup, all sections on one page.
 *
 * 6 sections in a single accordion:
 *   ① Auto-configure (one-click India defaults)
 *   ② Company & Legal (state, currency, home state)
 *   ③ Statutory rules (PF/ESIC/PT/LWF)
 *   ④ Salary Components & Structures
 *   ⑤ Employee Compensations (bulk assign)
 *   ⑥ Attendance & OT rules
 * Each section deep-links to its detailed CRUD page for advanced editing.
 */
const plural = (n, w) => `${n || 0} ${w}${(n || 0) === 1 ? "" : "s"}`;

export default function PayrollSetup() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [autoState, setAutoState] = useState("MH");
  const nav = useNavigate();

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/payroll/setup-guide/status");
      setStatus(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const autoConfigure = async () => {
    if (!window.confirm(`Auto-configure India payroll with state = ${autoState}?\n\nThis will:\n • Seed standard salary components (Basic, HRA, PF, ESIC, PT, TDS)\n • Create a "Standard India FTE" salary structure if none exists\n • Set company state to ${autoState}\n • Enable LWF for the chosen state\n\nExisting data will not be overwritten. Safe to run multiple times.`)) return;
    setAutoBusy(true);
    try {
      const r = await api.post("/payroll/auto-configure-india", { state_code: autoState });
      toast.success(`Configured — ${r.data.components_ready} components, "${r.data.structure_ready}" structure ready.`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setAutoBusy(false); }
  };

  const [recalcBusy, setRecalcBusy] = useState(false);
  const recalcAll = async () => {
    if (!window.confirm("Recalculate every employee's salary lines from their CTC and structure using the latest statutory rules?")) return;
    setRecalcBusy(true);
    try {
      const r = await api.post("/compensation/recalculate-all");
      toast.success(`Recalculated ${r.data.updated} salaries${r.data.skipped_no_structure ? ` · ${r.data.skipped_no_structure} without a structure skipped` : ""}.`);
      load();
    } catch { /* global toast */ } finally { setRecalcBusy(false); }
  };

  const stepsByKey = Object.fromEntries((status?.steps || []).map(s => [s.key, s]));
  const isDone = (key) => stepsByKey[key]?.done;

  // Setup sections
  const sections = [
    {
      k: "auto",
      title: "Auto-configure for India",
      subtitle: "One-click sensible defaults · run this first if you're new",
      required: false,
      done: isDone("components") && isDone("structures") && isDone("statutory"),
      body: (
        <div className="space-y-3">
          <div className="text-sm text-zinc-600">
            Sets up standard salary components, a "Standard India FTE" structure and enables
            statutory rules for your company state. You can fine-tune anything below afterwards.
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-500">Company state:</label>
            <select className="border border-zinc-300 rounded h-9 px-2 text-sm"
              value={autoState} onChange={(e) => setAutoState(e.target.value)}
              data-testid="auto-state">
              {STATE_OPTIONS.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select>
            <Button size="sm" onClick={autoConfigure} disabled={autoBusy}
              data-testid="auto-configure-btn"
              className="gap-1.5 bg-amber-500 hover:bg-amber-600 text-white">
              <Sparkle size={14} weight="fill"/> {autoBusy ? "Configuring…" : "Auto-configure now"}
            </Button>
          </div>
        </div>
      ),
    },
    {
      k: "components",
      title: "Salary Components & Structures",
      subtitle: `${plural(stepsByKey.components?.count, "component")} · ${plural(stepsByKey.structures?.count, "structure")}`,
      required: true,
      done: isDone("components") && isDone("structures"),
      body: (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600">
            Salary components (Basic, HRA, PF, ESIC…) are the building blocks. Structures combine them
            into reusable templates (e.g. "Standard India FTE", "Consultant Fixed").
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => nav("/app/payroll-legacy?tab=components")}
              data-testid="edit-components">
              Edit components <ArrowSquareOut size={12}/>
            </Button>
            <Button size="sm" variant="outline" onClick={() => nav("/app/payroll-legacy?tab=structures")}
              data-testid="edit-structures">
              Edit structures <ArrowSquareOut size={12}/>
            </Button>
          </div>
        </div>
      ),
    },
    {
      k: "ctc",
      title: "Employee Compensations",
      subtitle: `${stepsByKey.ctc?.count || 0} of ${stepsByKey.ctc?.total || 0} employees have a CTC`,
      required: true,
      done: isDone("ctc"),
      body: (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600">
            Assign each employee's CTC and salary structure. This is where their monthly gross &amp;
            take-home get computed from. Use CSV import for bulk assignment.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => nav("/app/payroll-legacy?tab=compensations")}
              data-testid="assign-ctc">
              Assign CTCs <ArrowSquareOut size={12}/>
            </Button>
            <Button size="sm" variant="outline" onClick={recalcAll} disabled={recalcBusy}
              data-testid="recalc-all-ctc">
              {recalcBusy ? "Recalculating…" : "Recalculate all salaries with latest rules"}
            </Button>
          </div>
          <p className="text-xs text-zinc-500">CTC includes employer PF, ESIC and gratuity — gross = CTC − employer costs. Recalculate after changing statutory rules.</p>
        </div>
      ),
    },
    {
      k: "statutory",
      title: "Statutory rules (PF · ESIC · PT · LWF · TDS)",
      subtitle: isDone("statutory") ? "Configured" : "State code & LWF pending",
      required: true,
      done: isDone("statutory"),
      body: (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600">
            Set your company's state (drives Professional Tax), LWF applicability, and PF/ESIC
            registration numbers. "Auto-configure" above pre-fills sensible defaults.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => nav("/app/statutory-config")}
              data-testid="edit-statutory">
              Statutory config <ArrowSquareOut size={12}/>
            </Button>
            <Button size="sm" variant="outline" onClick={() => nav("/app/company-settings")}
              data-testid="edit-company">
              Company settings <ArrowSquareOut size={12}/>
            </Button>
          </div>
        </div>
      ),
    },
    {
      k: "declarations",
      title: "Investment Declarations",
      subtitle: `${stepsByKey.declarations?.count || 0} of ${stepsByKey.declarations?.total || 0} employees submitted`,
      required: false,
      done: isDone("declarations"),
      body: (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600">
            Employees pick Old/New tax regime and declare 80C, HRA rent, home-loan interest, etc.
            More accurate TDS = fewer year-end refunds.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => nav("/app/declarations")}
              data-testid="view-declarations">
              Review declarations <ArrowSquareOut size={12}/>
            </Button>
          </div>
        </div>
      ),
    },
    {
      k: "bank",
      title: "Employee bank details",
      subtitle: `${stepsByKey.bank?.count || 0} of ${stepsByKey.bank?.total || 0} employees have a bank account on file`,
      required: true,
      done: isDone("bank"),
      body: (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600">
            Needed to generate the NEFT bank-advice CSV once payroll is finalised. Employees can add
            their own from Self-service.
          </p>
          <Button size="sm" variant="outline" onClick={() => nav("/app/employees")}
            data-testid="open-directory">
            Open employee directory <ArrowSquareOut size={12}/>
          </Button>
        </div>
      ),
    },
    {
      k: "ot",
      title: "Attendance & Overtime rules",
      subtitle: "Sunday multiplier, night allowance, full-attendance bonus",
      required: false,
      done: true,   // no strict requirement
      body: (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600">
            Optional per-day bonuses that automatically layer on top of the CTC when payroll runs
            (e.g. Sunday × 1.5, night shift × 1.0, ₹1000 for zero-absence months).
          </p>
          <Button size="sm" variant="outline" onClick={() => nav("/app/overtime-approvals")}
            data-testid="edit-bonus-rules">
            Attendance bonus rules <ArrowSquareOut size={12}/>
          </Button>
        </div>
      ),
    },
  ];

  const requiredDone = sections.filter(s => s.required && s.done).length;
  const requiredTotal = sections.filter(s => s.required).length;
  const readyToRun = requiredDone === requiredTotal;

  return (
    <AppShell title="Payroll setup" moduleKey="payroll">
      <div className="mb-4 flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={() => nav("/app/payroll")}
          data-testid="back-to-run">
          ← Back to Run payroll
        </Button>
      </div>

      {busy && !status ? (
        <div className="text-sm text-zinc-500 p-6">Loading…</div>
      ) : (
        <div className="space-y-4">
          {/* ── Progress banner ── */}
          <div className={`rounded-lg border p-5 ${readyToRun ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
            data-testid="setup-progress-banner">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {readyToRun
                    ? <CheckCircle size={22} weight="fill" className="text-emerald-600"/>
                    : <WarningCircle size={22} weight="fill" className="text-amber-600"/>}
                  <h2 className="text-lg font-semibold">
                    {readyToRun ? "You're ready to run payroll" : "One-time setup"}
                  </h2>
                </div>
                <p className="text-sm text-zinc-700">
                  <b>{requiredDone} of {requiredTotal}</b> required sections complete
                  {readyToRun ? " · nothing else needed here monthly." : " · finish these once, then every month is a single click."}
                </p>
              </div>
              {readyToRun && (
                <Button size="sm" onClick={() => nav("/app/payroll")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="goto-run-payroll">
                  Go to Run payroll →
                </Button>
              )}
            </div>
            <div className="mt-3 w-full h-2 bg-white/60 rounded-full overflow-hidden">
              <div className={`h-full transition-all ${readyToRun ? "bg-emerald-500" : "bg-amber-500"}`}
                style={{ width: `${Math.round(100 * requiredDone / requiredTotal)}%` }}/>
            </div>
          </div>

          {/* ── Accordion sections ── */}
          <div className="space-y-2">
            {sections.map((s, idx) => (
              <div key={s.k} className="border border-zinc-200 rounded-lg bg-white"
                data-testid={`setup-section-${s.k}`}>
                <button
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 rounded-lg"
                  onClick={() => setExpanded(expanded === s.k ? null : s.k)}
                  data-testid={`setup-toggle-${s.k}`}
                >
                  <div className="flex items-center gap-3 min-w-0 text-left">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm shrink-0
                      ${s.done ? "bg-emerald-500 text-white"
                              : (s.required ? "bg-amber-200 text-amber-900" : "bg-zinc-200 text-zinc-700")}`}>
                      {s.done ? "✓" : idx + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{s.title}</div>
                        {!s.required && <Badge variant="outline" className="text-[10px]">Optional</Badge>}
                        {s.required && !s.done && <Badge variant="outline" className="text-[10px] bg-amber-100 border-amber-300 text-amber-800">Required</Badge>}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5 truncate">{s.subtitle}</div>
                    </div>
                  </div>
                  {expanded === s.k ? <CaretDown size={16}/> : <CaretRight size={16}/>}
                </button>
                {expanded === s.k && (
                  <div className="px-4 pb-4 pt-1 border-t border-zinc-100">
                    {s.body}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
